// src/components/dashboard/panels/ActivityDetailPanel.jsx
import React from 'react';
import { ArrowRight, ChevronRight, Zap, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ActivityDetailPanel = ({ recentActivity, userProgress, onNavigate }) => {
  const { t } = useTranslation();

  if (!recentActivity) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('dashboard.actionZone.allCaughtUp.description')}</p>
      </div>
    );
  }

  // Get REAL previous sessions from userProgress
  const recentSessions = userProgress?.recentSessions?.slice(0, 3) || [];

  return (
    <>
      {/* Current Session Card */}
      <div
        className="group relative bg-gradient-to-br from-[#3A86FF] to-blue-700 text-white rounded-2xl p-6 overflow-hidden border-[3px] border-[#3A86FF]"
        style={{
          animation: 'slideInScale 0.5s ease-out',
          boxShadow: '0 20px 60px rgba(58, 134, 255, 0.3)'
        }}
      >
        {/* Decorative circles */}
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-white opacity-10 rounded-full transform translate-y-20 translate-x-20" />

        {/* Corner decorations */}
        <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-white/30" />
        <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-white/30" />

        <div className="relative z-10">
          <h3 className="font-space text-2xl md:text-3xl font-black mb-2 tracking-tight">{recentActivity.title}</h3>
          <p className="text-blue-200 mb-6">{recentActivity.subtitle}</p>

          {recentActivity.progress && (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-blue-200 font-medium">{t('dashboard.stats.progress')}</span>
                  <span className="font-space font-bold">
                    {recentActivity.progress.current}/{recentActivity.progress.total} ({recentActivity.progress.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-white h-4 rounded-full transition-all duration-500"
                    style={{width: `${recentActivity.progress.percentage}%`}}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <div className="bg-white/10 rounded-xl p-3 border border-white/20">
                  <p className="text-xs text-blue-200 mb-1 uppercase tracking-wider font-medium">{t('stats.learning.overallAccuracy')}</p>
                  <p className="font-space text-2xl font-black">
                    {userProgress?.grammarStats?.overallAccuracy || 0}%
                  </p>
                </div>
                <div className="bg-white/10 rounded-xl p-3 border border-white/20">
                  <p className="text-xs text-blue-200 mb-1 uppercase tracking-wider font-medium">{t('stats.main.studyTime')}</p>
                  <p className="font-space text-2xl font-black">
                    {Math.round((userProgress?.stats?.totalStudyTimeMinutes || 0) / 60)}h
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  // If it's a daily goal activity (type 'lesson' with progress), navigate to study plan
                  // Otherwise use the activity's path
                  const isDailyGoal = recentActivity.type === 'lesson' && recentActivity.progress;
                  const targetPath = isDailyGoal 
                    ? '/lessons' // Study plan page for daily goal
                    : (recentActivity.path || '/lessons');
                  onNavigate(targetPath);
                }}
                className="group/btn relative w-full bg-white text-[#3A86FF] py-4 rounded-xl font-space font-black text-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-xl border-2 border-white"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {t('dashboard.actionZone.startNow')}
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover/btn:translate-x-2" />
                </span>

                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-100 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
              </button>
            </div>
          )}
        </div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>

      {/* Previous Sessions - REAL DATA */}
      {recentSessions.length > 0 && (
        <div>
          <h4 className="font-space text-xl font-black text-black mb-4 tracking-tight">
            {t('stats.activity.title')}
          </h4>
          <div className="space-y-3">
            {recentSessions.map((session, i) => {
              const percentage = session.score && session.maxScore
                ? Math.round((session.score / session.maxScore) * 100)
                : session.completed ? 100 : 0;

              const progressColor = percentage >= 80 ? 'bg-black' : percentage >= 60 ? 'bg-gray-700' : 'bg-gray-500';

              return (
                <div
                  key={i}
                  className="group relative bg-white border-[3px] border-black/15 rounded-xl p-4 hover:border-[#3A86FF] hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                  onClick={() => onNavigate(session.path || '/lessons')}
                  style={{
                    animation: `slideInScale 0.5s ease-out ${i * 0.1}s both`
                  }}
                >
                  {/* Corner decorations */}
                  <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
                  <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />

                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h5 className="font-bold text-black group-hover:text-[#3A86FF] transition-colors">{session.title}</h5>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {formatSessionTime(session.createdAt, t)}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 group-hover:text-[#3A86FF] transition-all" />
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`${progressColor} h-2 rounded-full transition-all duration-500`}
                        style={{width: `${percentage}%`}}
                      />
                    </div>
                    {session.duration && (
                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {session.duration} {t('stats.activity.minutes')}
                      </p>
                    )}
                  </div>

                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3A86FF]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div
        className="bg-white rounded-2xl p-6 border-[3px] border-black/15"
        style={{
          animation: 'slideInScale 0.5s ease-out 0.3s both'
        }}
      >
        <h4 className="font-space text-lg font-black text-black mb-4 tracking-tight">{t('dashboard.quickActions.title')}</h4>
        <div className="space-y-2">
          <button
            onClick={() => onNavigate('/practice/grammar')}
            className="group relative w-full bg-white border-[3px] border-black text-black py-3 rounded-xl font-bold hover:bg-black hover:text-white transition-all duration-300 overflow-hidden"
          >
            <span className="relative z-10">{t('dashboard.quickActions.viewGrammar')}</span>
            <div className="absolute inset-0 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
          </button>
          <button
            onClick={() => onNavigate('/practice/vocabulary')}
            className="group relative w-full bg-white border-[3px] border-black text-black py-3 rounded-xl font-bold hover:bg-black hover:text-white transition-all duration-300 overflow-hidden"
          >
            <span className="relative z-10">{t('dashboard.quickActions.practiceVocabulary')}</span>
            <div className="absolute inset-0 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
          </button>
        </div>
      </div>
    </>
  );
};

// Helper: Format session time
const formatSessionTime = (timestamp, t) => {
  if (!timestamp) return t('dashboard.mistakes.timeAgo.daysAgo', { count: 0 });

  const now = new Date();
  const then = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 60) return t('dashboard.mistakes.timeAgo.minutesAgo', { count: diffMins });
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return t('dashboard.mistakes.timeAgo.hoursAgo', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  return t('dashboard.mistakes.timeAgo.daysAgo', { count: diffDays });
};

export default ActivityDetailPanel;